import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useGuestIdentity } from "../lib/guestIdentity";
import { useLocale } from "../lib/locale";
import { instagramHandleForInput, normalizeInstagramUrl } from "../lib/instagram";
import { emptyPaymentFields, hasPaymentMethod } from "../lib/payment";
import { normalizePhone } from "../lib/phone";
import { accountLinkedDancerId } from "../lib/dancerProfile";
import { validateHostPayment } from "../lib/profileForm";
import { createSchool } from "../lib/schools";
import { normalizeHostTheme } from "../lib/hostThemes";
import {
  accountFinishedHostOnboarding,
  clearHostOnboardingSession,
  completeHostOnboarding,
  createEventPathForOnboarding,
  deferHostOnboarding,
  hostOnboardingProgress,
  isHostOnboardingStep,
  markHostOnboardingActivated,
  readHostOnboardingSession,
  resumeHostOnboardingStep,
  writeHostOnboardingSession,
} from "../lib/hostOnboarding";
import BrandLink from "../components/BrandLink";
import { Button } from "../components/ui";
import Card from "../components/ui/Card";
import { FieldHint, FORM_STYLE_CARD_CLASS, SectionTitle } from "../components/createEvent/CreateEventFormUi";
import HostOnboardingProfileStep from "../components/onboarding/HostOnboardingProfileStep";
import HostOnboardingLoopStep from "../components/onboarding/HostOnboardingLoopStep";
import HostOnboardingSchoolStep from "../components/onboarding/HostOnboardingSchoolStep";
import HostOnboardingPaymentStep from "../components/onboarding/HostOnboardingPaymentStep";
import HostOnboardingThemeStep from "../components/onboarding/HostOnboardingThemeStep";
import HostOnboardingStickyActions, {
  ONBOARDING_DESKTOP_ACTIONS,
  ONBOARDING_STICKY_PAGE_PB,
} from "../components/onboarding/HostOnboardingStickyActions";

function stepHeadline(step, { activated, schoolSlug }) {
  if (step === "gate") return "Welcome to rsvp.dance";
  if (step === "profile") return "Your host profile";
  if (step === "loop") {
    if (activated || schoolSlug) return "You’re live";
    return "Get something live";
  }
  if (step === "school") return "Create your school";
  if (step === "payment") return "How students pay";
  return "Choose your look";
}

function stepSubcopy(step, { activated, schoolSlug }) {
  if (step === "gate") {
    return "About a minute if you’re hosting — or jump in as a guest.";
  }
  if (step === "profile") {
    return "A name is enough to start. Phone and Instagram help guests reach you.";
  }
  if (step === "loop") {
    if (schoolSlug) {
      return "School is up. Pick a look, open admin, or publish an event too.";
    }
    if (activated) {
      return "Your invite is live. Pick a look, or add a school when you’re ready.";
    }
    return "Publish an event or a school — either path gets guests something to open.";
  }
  if (step === "school") {
    return "Name and city first. Classes and venues come next, in school admin.";
  }
  if (step === "payment") {
    return "Optional. Share details on the app, keep payments off it, or skip.";
  }
  return "Optional. This theme shows on your public pages — change it anytime.";
}

function needsStickyPad(step) {
  return step === "gate" || step === "profile" || step === "school" || step === "payment" || step === "theme";
}

export default function OnboardingPage() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { account, checking, updateAccount } = useAuth();
  const { dancerId, refreshProfile, profile, bindDancerId } = useGuestIdentity();
  const locale = useLocale();

  const session = readHostOnboardingSession();
  const stepParam = searchParams.get("step");
  const resumeParam = searchParams.get("resume") === "1";
  const initialStep = (() => {
    if (isHostOnboardingStep(stepParam)) return stepParam;
    if (resumeParam) return resumeHostOnboardingStep(account, session);
    return session.step || "gate";
  })();

  const [step, setStep] = useState(initialStep);
  const [schoolSlug, setSchoolSlug] = useState(session.schoolSlug);
  const [activated, setActivated] = useState(session.activated || Boolean(session.schoolSlug));
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [personalInstagram, setPersonalInstagram] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [payment, setPayment] = useState(() => emptyPaymentFields(null));
  const [paymentMode, setPaymentMode] = useState(null); // "on_app" | "off_app" | null
  const [school, setSchool] = useState({
    name: "",
    city: "",
    city_location: null,
    bio: "",
    instagram: "",
    whatsapp_url: "",
  });

  const [themeId, setThemeId] = useState("warm-editorial");

  const schoolBranch =
    Boolean(schoolSlug) || step === "school" || step === "payment";

  const goStep = useCallback(
    (next, extra = {}) => {
      setStep(next);
      if (extra.activated !== undefined) setActivated(Boolean(extra.activated));
      if (extra.schoolSlug !== undefined) {
        setSchoolSlug(
          typeof extra.schoolSlug === "string" && extra.schoolSlug ? extra.schoolSlug : null
        );
      }
      writeHostOnboardingSession({ step: next, hostIntent: true, ...extra });
      const params = new URLSearchParams();
      params.set("step", next);
      setSearchParams(params, { replace: true });
    },
    [setSearchParams]
  );

  const finishAndExit = useCallback(
    async ({ toAdmin = false } = {}) => {
      setBusy(true);
      try {
        await completeHostOnboarding(updateAccount);
        const slug = schoolSlug || readHostOnboardingSession().schoolSlug;
        if (toAdmin && slug) {
          nav(`/admin/${slug}`, { replace: true });
        } else {
          nav("/events", { replace: true });
        }
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setBusy(false);
      }
    },
    [nav, schoolSlug, updateAccount]
  );

  /** Guest path — permanently skip host setup. */
  async function handleSkipAsGuest() {
    await finishAndExit({ toAdmin: false });
  }

  /** Host path — leave without completing so the feed can resume. */
  function handleFinishLater() {
    deferHostOnboarding({
      step: step === "gate" ? "profile" : step,
      schoolSlug,
      activated,
      hostIntent: true,
    });
    nav("/events", { replace: true });
  }

  // Signed-in only. Completed hosts who reopen /onboarding bounce to events
  // unless they have an explicit ?step= (rare re-entry).
  useEffect(() => {
    if (checking) return;
    if (!account) {
      nav(`/login?next=${encodeURIComponent("/onboarding")}`, { replace: true });
      return;
    }
    if (accountFinishedHostOnboarding(account) && !stepParam && !resumeParam) {
      nav("/events", { replace: true });
    }
  }, [account, checking, nav, stepParam, resumeParam]);

  // Seed profile fields from account / dancer
  useEffect(() => {
    if (!account) return;
    setName((n) => n || account.name || "");
    setPersonalInstagram((ig) => ig || instagramHandleForInput(account.personal_instagram) || "");
    setContactPhone((p) => p || account.phone || "");
    setThemeId(normalizeHostTheme(account.theme));
    setPayment(emptyPaymentFields(account));
    if (account.school_name) {
      setSchool((s) => ({
        ...s,
        name: s.name || account.school_name || "",
        city: s.city || account.school_city || "",
        city_location: s.city_location || account.school_city_location || null,
        bio: s.bio || account.school_bio || "",
        instagram: s.instagram || account.school_instagram || "",
      }));
    }
  }, [account]);

  useEffect(() => {
    if (!profile) return;
    if (profile.name) setName((n) => n || profile.name);
    if (profile.phone) setContactPhone((p) => p || profile.phone);
  }, [profile]);

  useEffect(() => {
    if (!dancerId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/dancers/${dancerId}`);
        if (cancelled || !r.data) return;
        if (r.data.name) setName((n) => n || r.data.name);
        if (r.data.phone) setContactPhone((p) => p || r.data.phone);
      } catch {
        /* optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dancerId]);

  // Resolve ?resume=1 once into a concrete ?step=
  useEffect(() => {
    if (!resumeParam || !account) return;
    const next = resumeHostOnboardingStep(account, readHostOnboardingSession());
    writeHostOnboardingSession({ step: next, hostIntent: true });
    setStep(next);
    const sess = readHostOnboardingSession();
    setActivated(sess.activated || Boolean(sess.schoolSlug));
    if (sess.schoolSlug) setSchoolSlug(sess.schoolSlug);
    const params = new URLSearchParams();
    params.set("step", next);
    setSearchParams(params, { replace: true });
  }, [resumeParam, account, setSearchParams]);

  // Sync URL ↔ step when landing with ?step= from event create
  useEffect(() => {
    if (resumeParam) return;
    if (!isHostOnboardingStep(stepParam)) return;
    if (stepParam === step) return;
    setStep(stepParam);
    const sess = readHostOnboardingSession();
    setActivated(sess.activated || Boolean(sess.schoolSlug));
    if (sess.schoolSlug) setSchoolSlug(sess.schoolSlug);
    writeHostOnboardingSession({ step: stepParam });
  }, [stepParam, step, resumeParam]);

  function handleHostYes() {
    goStep("profile", { hostIntent: true });
  }

  async function saveProfile() {
    const trimmedName = name.trim();
    const phone = normalizePhone(contactPhone) || null;
    setBusy(true);
    try {
      const paymentSeed = emptyPaymentFields(account);
      const r = await api.patch("/organizers/me", {
        name: trimmedName,
        personal_instagram: normalizeInstagramUrl(personalInstagram) || null,
        ...(phone
          ? {
              phone,
              default_payment: {
                ...paymentSeed,
                contact_phone: phone,
              },
            }
          : {}),
      });
      updateAccount(r.data.organizer);

      if (dancerId) {
        await api.patch(`/dancers/${dancerId}`, {
          name: trimmedName,
          phone,
        });
        await refreshProfile();
      } else if (account?.id && phone) {
        const linkedId = accountLinkedDancerId(account.id);
        await api.patch(`/dancers/${linkedId}`, {
          name: trimmedName,
          phone,
        });
        bindDancerId(linkedId);
        await refreshProfile();
      }
      goStep("loop");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function saveSchool() {
    const errors = {};
    const schoolName = (school.name || "").trim();
    if (!schoolName) errors.school_name = "Enter a school name.";
    if (!school.city?.trim()) errors.school_city = "Enter your school city.";
    else if (!school.city_location) errors.school_city = "Select your city from the suggestions.";
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setBusy(true);
    try {
      const orgPayload = {
        runs_school: true,
        school_name: schoolName,
        school_city: school.city.trim(),
        school_city_location: school.city_location,
        school_bio: (school.bio || "").trim() || null,
        school_instagram: normalizeInstagramUrl(school.instagram),
      };
      const r = await api.patch("/organizers/me", orgPayload);
      updateAccount(r.data.organizer);

      const created = await createSchool({
        name: schoolName,
        description: (school.bio || "").trim() || undefined,
        city: school.city.trim() || undefined,
        instagram: normalizeInstagramUrl(school.instagram) || undefined,
        whatsapp_url: (school.whatsapp_url || "").trim() || undefined,
      });
      markHostOnboardingActivated({ schoolSlug: created.slug, step: "payment" });
      setSchoolSlug(created.slug);
      setActivated(true);
      setPaymentMode(null);
      toast.success("School created — you’re live");
      goStep("payment", { schoolSlug: created.slug, activated: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function savePaymentPreference({ skip = false } = {}) {
    if (!skip && !paymentMode) return;
    const errors = {};
    if (!skip && paymentMode === "on_app") {
      Object.assign(errors, validateHostPayment(payment));
    }
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setBusy(true);
    try {
      if (!skip) {
        const shareOnApp = paymentMode === "on_app";
        const hasMethod = shareOnApp && hasPaymentMethod(payment);
        const orgPayload = {
          hosts_paid: hasMethod,
        };
        if (shareOnApp) {
          orgPayload.default_payment = {
            payment_link: payment.payment_link || "",
            payment_upi: payment.payment_upi || "",
            instructions: payment.instructions || "",
            contact_phone: payment.contact_phone || "",
            contact_email: payment.contact_email || "",
            contact_telegram: payment.contact_telegram || "",
          };
        }
        const r = await api.patch("/organizers/me", orgPayload);
        updateAccount(r.data.organizer);
      }
      goStep("theme");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function finishTheme() {
    setBusy(true);
    try {
      const r = await api.patch("/organizers/me", {
        theme: themeId,
        onboarding_completed: true,
      });
      updateAccount(r.data.organizer);
      const slug = schoolSlug || readHostOnboardingSession().schoolSlug;
      clearHostOnboardingSession();
      if (slug) {
        nav(`/admin/${slug}`, { replace: true });
      } else {
        nav("/events", { replace: true });
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  if (checking || !account) {
    return (
      <div className="min-h-screen bg-canvas text-ink font-body flex items-center justify-center px-4">
        <p className="font-ui text-[14px] text-ink/70" role="status">
          Getting things ready…
        </p>
      </div>
    );
  }

  const progress = hostOnboardingProgress(step, { schoolBranch });
  const headlineCtx = { activated, schoolSlug };

  const stickyPad = needsStickyPad(step);

  return (
    <div className="min-h-screen bg-canvas text-ink font-body" data-testid="host-onboarding-page">
      <header className="max-w-page mx-auto w-full px-4 pt-4 pb-2 flex items-center justify-between gap-3">
        <BrandLink to="/events" variant="readable" size="sm" testId="onboarding-brand-link" aria-label="rsvp.dance" />
        {step !== "gate" ? (
          <Button
            type="button"
            variant="link"
            size="sm"
            disabled={busy}
            onClick={handleFinishLater}
            data-testid="host-onboarding-skip-setup"
            className="min-h-[44px] px-2 shrink-0"
            title="Leave for now. We’ll remind you on your feed so you can finish later."
          >
            Finish later
          </Button>
        ) : null}
      </header>

      <main
        className={`max-w-page mx-auto px-4 pt-5 sm:pt-7 ${
          stickyPad ? ONBOARDING_STICKY_PAGE_PB : "pb-[max(2rem,env(safe-area-inset-bottom))]"
        }`}
      >
        {step !== "gate" ? (
          <div
            className="mb-5 flex items-center gap-2.5"
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={progress.max}
            aria-valuenow={Math.max(progress.current, 1)}
            aria-label={`${progress.label}: step ${progress.current} of ${progress.max}`}
            data-testid="host-onboarding-progress"
          >
            <span className="font-ui text-[12px] font-medium text-ink/70 shrink-0 tabular-nums">
              {progress.current}/{progress.max}
            </span>
            <div className="flex flex-1 items-center gap-1.5" aria-hidden>
              {Array.from({ length: progress.max }, (_, i) => (
                <span
                  key={i}
                  className={`h-1.5 flex-1 rounded-full motion-safe:transition-colors motion-safe:duration-200 ${
                    i < progress.current ? "bg-ink" : "bg-black/[0.08]"
                  }`}
                />
              ))}
            </div>
            <span className="font-ui text-[12px] font-medium text-ink/55 shrink-0 max-w-[7rem] truncate">
              {progress.label}
            </span>
          </div>
        ) : null}

        <h1 className="font-ui font-semibold text-[22px] sm:text-[24px] leading-tight tracking-[-0.01em] text-balance">
          {stepHeadline(step, headlineCtx)}
        </h1>
        <p className="mt-2 font-ui text-[14px] sm:text-[15px] text-ink/80 leading-relaxed text-pretty max-w-[65ch]">
          {stepSubcopy(step, headlineCtx)}
        </p>

        <div className="mt-6">
          {step === "gate" ? (
            <>
              <Card className={FORM_STYLE_CARD_CLASS} data-testid="host-onboarding-gate">
                <SectionTitle>Are you hosting?</SectionTitle>
                <FieldHint className="text-[13px]">
                  Hosts publish events or run a school. Guests can skip setup and RSVP.
                </FieldHint>
                <div className={`${ONBOARDING_DESKTOP_ACTIONS} mt-5 !justify-start gap-2`}>
                  <Button
                    type="button"
                    variant="primary"
                    size="lg"
                    disabled={busy}
                    onClick={handleHostYes}
                    data-testid="host-onboarding-yes"
                  >
                    Yes — I’m hosting
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="lg"
                    disabled={busy}
                    onClick={handleSkipAsGuest}
                    data-testid="host-onboarding-no"
                  >
                    Just browsing
                  </Button>
                </div>
              </Card>
              <HostOnboardingStickyActions testId="host-onboarding-gate-sticky">
                <div className="flex w-full flex-col gap-2">
                  <Button
                    type="button"
                    variant="primary"
                    size="lg"
                    disabled={busy}
                    onClick={handleHostYes}
                    data-testid="host-onboarding-yes-sticky"
                    className="w-full"
                  >
                    Yes — I’m hosting
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="lg"
                    disabled={busy}
                    onClick={handleSkipAsGuest}
                    data-testid="host-onboarding-no-sticky"
                    className="w-full"
                  >
                    Just browsing
                  </Button>
                </div>
              </HostOnboardingStickyActions>
            </>
          ) : null}

          {step === "profile" ? (
            <HostOnboardingProfileStep
              name={name}
              onNameChange={setName}
              contactPhone={contactPhone}
              onContactPhoneChange={setContactPhone}
              personalInstagram={personalInstagram}
              onPersonalInstagramChange={setPersonalInstagram}
              fieldErrors={fieldErrors}
              onSetFieldErrors={setFieldErrors}
              onClearFieldError={(key) =>
                setFieldErrors((prev) => {
                  if (!prev[key]) return prev;
                  const next = { ...prev };
                  delete next[key];
                  return next;
                })
              }
              saving={busy}
              onSave={saveProfile}
            />
          ) : null}

          {step === "loop" ? (
            <HostOnboardingLoopStep
              schoolSlug={schoolSlug}
              activated={activated}
              busy={busy}
              onCreateEvent={() => {
                writeHostOnboardingSession({ step: "loop", hostIntent: true });
                nav(createEventPathForOnboarding());
              }}
              onCreateSchool={() => {
                setFieldErrors({});
                setPaymentMode(null);
                goStep("school");
              }}
              onContinueToTheme={() => goStep("theme")}
              onFinish={() => finishAndExit({ toAdmin: Boolean(schoolSlug) })}
            />
          ) : null}

          {step === "school" ? (
            <HostOnboardingSchoolStep
              school={school}
              onSchoolChange={setSchool}
              fieldErrors={fieldErrors}
              cityPlaceholder={locale.default_city || "Berlin"}
              saving={busy}
              onBack={() => goStep("loop")}
              onSubmit={saveSchool}
            />
          ) : null}

          {step === "payment" ? (
            <HostOnboardingPaymentStep
              mode={paymentMode}
              onModeChange={(next) => {
                setPaymentMode(next);
                setFieldErrors({});
              }}
              payment={payment}
              onPaymentChange={(next) => {
                setFieldErrors((prev) => {
                  const updated = { ...prev };
                  delete updated.contact_phone;
                  delete updated.contact_email;
                  delete updated.payment;
                  return updated;
                });
                setPayment(next);
              }}
              fieldErrors={fieldErrors}
              payeeName={name || account.name}
              schoolName={school.name || account.school_name}
              saving={busy}
              onBack={() => {
                setFieldErrors({});
                goStep("loop");
              }}
              onContinue={() => savePaymentPreference({ skip: false })}
              onSkip={() => savePaymentPreference({ skip: true })}
            />
          ) : null}

          {step === "theme" ? (
            <HostOnboardingThemeStep
              themeId={themeId}
              onThemeChange={setThemeId}
              saving={busy}
              onFinish={finishTheme}
              finishLabel={schoolSlug ? "Open school admin →" : "Go to my events →"}
            />
          ) : null}
        </div>

        {step === "gate" ? (
          <p className="mt-8 font-ui text-[13px] text-ink/55 text-center">
            Already set up?{" "}
            <Link to="/events" className="underline underline-offset-2 text-ink/70 hover:text-ink">
              Go to events
            </Link>
          </p>
        ) : null}
      </main>
    </div>
  );
}
